import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import WebflowPublishNotice from './WebflowPublishNotice';
import { authFetch } from '../utils/api';
import { decodeTokenClaims } from '../utils/session';
import { popupBusy } from '../utils/popupLock';
import './WebflowPublishPending.css';

// Webflow sites whose live version still runs older agent code: an update staged for the next publish, e.g. the
// move of every installation to a new widget release (GET /api/webflow/publish_pending, from Webflow's own
// timestamps). The owner gets the same Publish button as after an activation (Webflow Marketplace: the App prompts,
// the customer publishes; Robert 30.10.2026). Floating, so nothing on the page moves. Once per visit: the X hides
// it until the next visit. Never in a master-login session, inside the Wix dashboard, during onboarding or on top of
// a self-opening popup (utils/popupLock).

const HIDDEN_KEY = 'ub_wf_publish_pending_hidden';
const QUIET_PATHS = ['/agents/new', '/wix', '/wordpress-register', '/webflow-register', '/create-chatbot', '/mcp', '/login', '/register'];
const DELAY_MS = 1200;
const DONE_MS = 4000;

function hiddenThisVisit() {
  try { return sessionStorage.getItem(HIDDEN_KEY) === '1'; } catch (e) { return false; }
}

export default function WebflowPublishPending({ isWixSite = false }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [sites, setSites] = useState([]);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(hiddenThisVisit);

  useEffect(() => {
    if (isWixSite || hidden || decodeTokenClaims(localStorage.getItem('token'))?.via === 'master') return undefined;
    let alive = true;
    let timer;
    const showWhenFree = () => {
      if (!alive) return;
      if (popupBusy('webflow_publish_pending')) { timer = setTimeout(showWhenFree, 1000); return; }
      setReady(true);
    };
    timer = setTimeout(async () => {
      try {
        const r = await authFetch(`${process.env.REACT_APP_API_BASE_URL}webflow/publish_pending`);
        const j = r && r.ok ? await r.json() : null;
        if (!alive || !j?.sites?.length) return;
        setSites(j.sites);
        showWhenFree();
      } catch (e) { /* a missed check shows nothing; the next visit asks again */ }
    }, DELAY_MS);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready || hidden || !sites.length || QUIET_PATHS.some((p) => pathname.startsWith(p))) return null;

  const close = () => {
    try { sessionStorage.setItem(HIDDEN_KEY, '1'); } catch (e) { /* no storage: hidden for this page view */ }
    setHidden(true);
  };
  const published = (siteId) => setTimeout(() => setSites((s) => s.filter((x) => x.site_id !== siteId)), DONE_MS);

  return (
    <aside className="wf-publish-pending" aria-label={t('wf_publish_update_label')}>
      <button type="button" className="wf-publish-pending__close" onClick={close} aria-label={t('close')}>
        <FontAwesomeIcon icon={faXmark} />
      </button>
      {sites.map((s) => (
        <WebflowPublishNotice
          key={s.site_id}
          botId={s.bot_id}
          siteId={s.site_id}
          siteName={s.site_name}
          title={t('wf_publish_update_title', { siteName: s.site_name || 'Webflow' })}
          text={t('wf_publish_update_text')}
          onPublished={() => published(s.site_id)}
        />
      ))}
    </aside>
  );
}
